import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { ConfigCacheService } from '@services/api/config-cache.service';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private api: ApiService,
    private configCache: ConfigCacheService
  ) {}

  /** Office staff roster changed — the byoffice dropdown and office-staff-list grid are stale. */
  private invalidateStaffCaches(): void {
    this.configCache.evict('/users/byoffice');
    this.configCache.evict('/users/office-staff-list');
  }

  getUsers = (payload: any) => {
    return this.api.get('/users', payload);
  };

  saveUsers = (payload: any) => {
    return this.api.post('/users', payload).pipe(tap(() => this.invalidateStaffCaches()));
  };

  getUserById = (id: any, inc: any = '') => {
    return this.api.get('/users/' + id + inc);
  };

  updateUser = (id: any, payload: any) => {
    return this.api.put('/users/' + id, payload).pipe(tap(() => this.invalidateStaffCaches()));
  };

  getContactUser = (id: any) => {
    return this.api.get('/contacts/getbyid/' + id);
  };

  /** Search contacts (same `/search-data` endpoint the re-multiselect uses) — e.g. to match an email to an existing contact. */
  searchContacts = (keyword: string) => {
    return this.api.get(
      `/search-data?office_id=${this.api.getOfficeId()}&exclude_solds=true&Keywords=${encodeURIComponent(
        keyword ?? ''
      )}&module=contacts&type=Contact&contact_type=&company_type=&page=1&limit=20&project_id=[]&listing_id=[]`
    );
  };

  getUserByRoleId = (id: any) => {
    return this.api.get(`/users?role=${id}&search=&sort=&office_id=${this.api.getOfficeId()}`);
  };

  getContactList = () => {
    return this.api.get(`/contacts?office_id=${this.api.getOfficeId()}&user_type=${'Contact'}&uid=${this.api.getUserId()}&is_short=${true}`);
  };

  getContactListV1 = () => {
    return this.api.get(
      `/contacts?office_id=${this.api.getOfficeId()}&user_type=${'Contact'}&uid=${this.api.getUserId()}&add_company_contact=${true}&contact_type=${JSON.stringify(['Developers'])}&is_short=${true}`
    );
  };

  getRoleById(id) {
    return this.api.get(`/roles/` + id);
  }

  updateContact = (id: any, payload: any) => {
    return this.api.put('/contacts/' + id, payload);
  };

  createContact = (payload: any) => {
    return this.api.post('/contacts', payload);
  };

  trashContact = (row_id: any, payload: any) => {
    return this.api.put('/contacts/trash-or-recover/' + row_id + '?office_id=' + this.api.getOfficeId(), payload);
  };

  getContacts = (payload: any) => {
    return this.api.get(`/contacts/list?office_id=${this.api.getOfficeId()}&uid=${this.api.getUserId()}`, payload);
  };

  getContactAppointment = (value: any) => {
    return this.api.get(`/fetch/contact/appointments/${value}`);
  };

  getSplitUser = () => {
    return this.api.get(`/views/splitter/${this.api.getUserId()}?office_id=${this.api.getOfficeId()}&type=${'contact'}`);
  };

  splitUser = (payload: any) => {
    return this.api.post('/views/splitter', payload);
  };

  getOfficeStaff = (page: any, per_page: any, role: any, query: any, uid: any, officeid: any, SortBy: any) => {
    return this.api.get(
      `/users/office-staff-list?page=${page}&per_page=${per_page}&role=${role}&search=${query}&uid=${uid}&officeid=${officeid}&sort=${SortBy}&show=staff&include=groups,offices`
    );
  };

  getContactCampaigns = (parms: any) => {
    return this.api.get('/contacts/companies-of-contact?contact_id=' + parms);
  };

  getContactTasks = (id: any) => {
    return this.api.get('/contacts/tasks/' + id);
  };

  createRelatedContacts = (payload: any) => {
    return this.api.post('/related-contacts', payload);
  };

  getRelatedContacts = (id: any) => {
    return this.api.get('/related-contacts/' + id);
  };

  deleteRelatedContacts = (id: any) => {
    return this.api.delete('/related-contacts/' + id);
  };
  deleteArchiveContact = (id: any) => {
    return this.api.delete('/contacts/' + id);
  };
  deleteMultipleContact = (payload: any) => {
    return this.api.post('/contacts/delete-multiple', payload);
  };
  recoverMultipleContact = (payload: any) => {
    return this.api.post('/contacts/recover-multiple', payload);
  };

  getPropertyContact = (id: any) => {
    return this.api.get('/contacts/property_contact/' + id + '?office_id=' + this.api.getOfficeId());
  };

  savePropertyContact = (payload: any) => {
    return this.api.post('/contact-association', payload);
  };

  deletePropertyContact = (id: any) => {
    return this.api.delete('/contact-association/' + id);
  };

  updatePropertyContact = (id: any, payload: any) => {
    return this.api.put('/contact-association/' + id, payload);
  };

  deletePropertyContactNow = (id: any, conId: any) => {
    return this.api.delete(`/contact-association/${id}?contact_id=${conId}&is_property=${true}`);
  };

  makePrivateContact = (id: any, payload: any) => {
    return this.api.put('/contacts/is_private/' + id, payload);
  };

  shareContact = (payload: any) => {
    return this.api.post('/contacts/share', payload);
  };

  ownContact = (id: any, payload: any) => {
    return this.api.put('/contacts/owner/' + id, payload);
  };

  editContact = (id: any, payload: any) => {
    return this.api.put('/contacts/editor/' + id, payload);
  };

  getSharedContact = (id: any) => {
    return this.api.get('/contacts/share/' + id);
  };

  requestEditingContact = (id: any) => {
    return this.api.get('/contacts/request-editing/' + id);
  };

  getContactStats = (id: any) => {
    return this.api.get(`/contacts/getContactStats/${id}`);
  };

  getAssociatedProjects = (id: any) => {
    return this.api.get(`/contact-project-association-detail/${id}`);
  };

  getUserAssociatedProjects = (id: any, office_id: string = '') => {
    return this.api.get(`/user-project-association-detail/${id}?office_id=${office_id}`);
  };

  getAgreementProjects = (id: any) => {
    return this.api.get(`/contact-project-agreement-detail/${id}`);
  };

  associateUserToProject = (payload: any) => {
    return this.api.post(`/contact-project-association`, payload);
  };

  associateUserToProjectTwo = (payload: any) => {
    return this.api.post(`/user-project-association`, payload);
  };

  deleteAssociateUserToProjectTwo = (payload: any) => {
    return this.api.delete(`/user-project-association-remove`, payload);
  };

  deletedAssociateUserToProject = (payload: any) => {
    return this.api.delete(`/contact-project-association-remove`, payload);
  };

  getUserTeamAssociation = (id: any) => {
    return this.api.get(`/user-team-association-detail/${id}?office_id=${this.api.getOfficeId()}`);
  };

  updateContactTags = (id: any, payload: any) => {
    return this.api.put('/related-contacts/' + id, payload);
  };

  sendEmailToUser = (payload: any) => {
    return this.api.post('/send-email-to-user', payload);
  };

  getOfficeRolesCount = (isMaster: boolean) => {
    const officeId = this.api.getOfficeId();
    const query = isMaster ? '' : '?show=staff';
    const endpoint = `/roles/user-summary/${officeId}${query}`;
    return this.api.get(endpoint);
  };

  getUserByRole = () => {
    let role = JSON.stringify(['Sales Agent', 'Operations Manager', 'Administrators']);
    return this.api.get('/users/by-role-name?office=' + this.api.getOfficeId() + '&role=' + role);
  };

  deleteMultipleUsers = (payload: any) => {
    return this.api.post('/users/action', payload).pipe(tap(() => this.invalidateStaffCaches()));
  };

  getGroupsAssign = () => {
    return this.api.get('/groups_assign?per_page=all');
  };

  getMyOffices = () => {
    return this.api.get('/offices/my?per_page=all');
  };

  getOffices = (page: any, per_page: any) => {
    return this.api.get('/offices', { page: page, per_page: per_page, few_details: true });
  };

  deleteMultiOffices = (payload: any) => {
    return this.api.delete('/offices/action', payload);
  };
  getOfficeData = () => {
    return this.api.get('/offices/' + this.api.getOfficeId());
  };
  getOfficeById = (id: any, inc: any = '') => {
    return this.api.get(`/offices/${id}` + inc);
  };

  /** Office Information card only — returns just first_name, last_name, email, mobile, website, address_line_1. */
  getOfficeInfo = (id: any) => {
    return this.api.get(`/offices/${id}/info`);
  };

  /** Master-admin catalog: AI vendors (openai, google, anthropic + labels). Uses global Bearer JWT. */
  getOfficeAiProviders = () => {
    return this.api.get(`/offices/ai-providers`);
  };

  /**
   * Master-admin catalog: models per provider or full catalog.
   * @param provider optional lowercase id (e.g. openai); omit for all providers + flat list.
   */
  getOfficeAiModels = (provider?: string) => {
    const params = provider ? { provider } : {};
    return this.api.get(`/offices/ai-models`, params);
  };

  saveOffice = (payload: any) => {
    return this.api.post(`/offices`, payload);
  };

  saveTeams = (payload: any) => {
    return this.api.post(`/teams`, payload);
  };

  getTeams = () => {
    return this.api.get('/teams?include=users&office_id=' + this.api.getOfficeId());
  };
  getUserTeamList = () => {
    return this.api.get(
      `/teams?filter=user&office_id=${this.api.getOfficeId()}&user_id=${this.api.getUserId()}&include=${'users'}&per_page=${'all'}`
    );
  };
  getTeamAssociatedProjects = (editId: any) => {
    return this.api.get(`/team-project-association-detail/${editId}`);
  };

  saveTeamAssociatedProjects = (payload: any) => {
    return this.api.post(`/team-project-association`, payload);
  };

  deleteTeamAssociatedProjects = (payload: any) => {
    return this.api.delete(`/team-project-association-remove`, payload);
  };

  deleteTeams = (id: any) => {
    return this.api.delete(`/teams/${id}`);
  };

  getTeamById = (id: any, inc: any = '') => {
    return this.api.get(`/teams/${id}` + inc);
  };

  getTeamMemberIds = (id: any) => {
    return this.api.get(`/teams/members?teams=${id}`);
  };

  updateTeam = (id: any, payload: any) => {
    return this.api.put(`/teams/${id}`, payload);
  };

  updateOffice = (id: any, payload: any) => {
    return this.api.put(`/offices/${id}`, payload);
  };

  /** Office Information card only — accepts the same 6 fields as getOfficeInfo(); any other key is ignored. */
  updateOfficeInfo = (id: any, payload: any) => {
    return this.api.put(`/offices/${id}/info`, payload);
  };

  deleteOfficeLogo = (id: any) => {
    return this.api.delete(`/offices/logo/${id}`);
  };

  deleteContactCompany(payload) {
    return this.api.put('/contacts/unassign-from-company/', payload);
  }

  getGroups = (page: any, per_page: any, inc: any = '') => {
    return this.api.get(`/groups?page=${page}&per_page=${per_page}` + inc);
  };

  updateGroup = (id: any, payload: any) => {
    return this.api.put(`/groups/${id}`, payload);
  };

  saveGroup = (payload: any) => {
    return this.api.post('/groups', payload);
  };

  getGroupById = (id: any, inc: any = '') => {
    return this.api.get(`/groups/${id}` + inc);
  };

  groupAction = (payload: any) => {
    return this.api.delete('/groups/delete-many', payload);
  };

  /** Absences for one staff member — holiday and sickness both live here. */
  getStaffHolidays = (staffId: any) => {
    return this.api.get(`/staff-holidays/${staffId}?office_id=${this.api.getOfficeId()}`);
  };

  deleteStaffHoliday = (id: any) => {
    return this.api.delete(`/staff-holidays/${id}`);
  };

  addStaffHoliday = (payload: any) => {
    return this.api.post('/staff-holidays/add', payload);
  };

  addCompanyToContact(payload) {
    return this.api.post('/contacts/add-into-company', payload);
  }

  addRelatedContact(payload) {
    return this.api.post('/related-contacts', payload);
  }

  getContactCompanies = (payload: any) => {
    return this.api.post('/contacts/companies-contacts', payload);
  };
}
